import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import AISkeletonLoader from "./AISkeletonLoader";
import { ConversationMessage } from "../context/StreamingContext";
import "./RecommendationCard.css";

interface RecommendationCardProps {
  initialQuestion?: string;
  content: string;
  isLoading: boolean;
  conversationMessages?: ConversationMessage[];
  currentStreamingMessage?: string;
  relatedQuestions?: string[];
  isLoadingFollowUp?: boolean;
  onQuestionClick?: (question: string) => void;
  onRegenerate?: () => void;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  initialQuestion,
  content,
  isLoading,
  conversationMessages = [],
  currentStreamingMessage = "",
  relatedQuestions = [],
  isLoadingFollowUp = false,
  onQuestionClick,
  onRegenerate,
}) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyLatest = () => {
    let textToCopy = content;
    const aiMessages = conversationMessages.filter((m) => m.type === "ai");
    if (aiMessages.length > 0) {
      textToCopy = aiMessages[aiMessages.length - 1].content;
    }
    navigator.clipboard.writeText(textToCopy);

    // Show visual feedback
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };
  return (
    <div className="chat-feed">
      {/* Initial User Question */}
      {initialQuestion && (
        <div className="message-row user-row">
          <div className="user-message-bubble">{initialQuestion}</div>
        </div>
      )}

      {/* Main AI Recommendation Card */}
      <div className="message-row ai-row">
        {isLoading && !content ? (
          <AISkeletonLoader />
        ) : (
          <div className="recommendation-card">
            <div className="card-header">
              <img
                src="/ai-chat-icon.png"
                alt="AI Icon"
                width="40"
                height="35"
                className="ai-icon"
              />
              <h2>AI Clinical Recommendation</h2>
            </div>

            <div className="card-content">
              {content ? (
                <div className="markdown-content">
                  <ReactMarkdown>{content}</ReactMarkdown>
                </div>
              ) : (
                <div className="no-content">Waiting for AI response...</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Subsequent Conversation Messages */}
      {conversationMessages.map((message, index) => (
        <div
          key={index}
          className={`message-row ${message.type === "user" ? "user-row" : "ai-row"}`}
        >
          {message.type === "user" ? (
            <div className="user-message-bubble">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
          ) : (
            <div className="recommendation-card secondary-response">
              <div className="card-header">
                <img
                  src="/ai-chat-icon.png"
                  alt="AI Icon"
                  width="40"
                  height="35"
                  className="ai-icon"
                />
                <h2>AI Clinical Recommendation</h2>
              </div>
              <div className="card-content">
                <div className="markdown-content">
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                </div>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Current Streaming Message */}
      {currentStreamingMessage && (
        <div className="message-row ai-row">
          <div className="recommendation-card secondary-response streaming">
            <div className="card-header">
              <img
                src="/ai-chat-icon.png"
                alt="AI Icon"
                width="40"
                height="35"
                className="ai-icon"
              />
              <h2>AI Clinical Recommendation</h2>
            </div>
            <div className="card-content">
              <div className="markdown-content">
                <ReactMarkdown>{currentStreamingMessage}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Section: Related Questions + Global Actions */}
      {!isLoading && !currentStreamingMessage && (
        <div className="chat-footer-section">
          {/* Related Questions - Right Aligned, 80% width */}
          <div className="related-questions-horizontal-wrapper">
            {isLoadingFollowUp ? (
              <div className="related-questions">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="question-button skeleton-question">
                    <span
                      className="skeleton-line"
                      style={{ width: "90%", height: "14px" }}
                    ></span>
                  </div>
                ))}
              </div>
            ) : Array.isArray(relatedQuestions) &&
              relatedQuestions.length > 0 ? (
              <div className="related-questions">
                {relatedQuestions.map((q, i) => (
                  <button
                    key={i}
                    className="question-button"
                    onClick={() => onQuestionClick?.(q)}
                  >
                    <span className="plus-icon">+</span>
                    <span className="question-text">{q}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          {/* Global Actions: Copy & Regenerate */}
          {(content || conversationMessages.length > 0) && (
            <div className="global-actions">
              <button
                className={`action-btn ${isCopied ? "copied" : ""}`}
                onClick={handleCopyLatest}
                title="Copy Latest Response"
              >
                {isCopied ? (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#006241"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="9"
                      y="9"
                      width="13"
                      height="13"
                      rx="2"
                      ry="2"
                    ></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                )}
              </button>
              <button
                className="action-btn"
                onClick={onRegenerate}
                title="Regenerate"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M23 4v6h-6"></path>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                </svg>
              </button>
            </div>
          )}
        </div>
      )}
      {/* <div className="card-footer">
          <button className="footer-button" aria-label="Copy">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.333 6h-6C6.597 6 6 6.597 6 7.333v6c0 .737.597 1.334 1.333 1.334h6c.737 0 1.334-.597 1.334-1.334v-6c0-.736-.597-1.333-1.334-1.333z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.333 10h-.666a1.333 1.333 0 01-1.334-1.333v-6a1.333 1.333 0 011.334-1.334h6A1.333 1.333 0 0110 2.667v.666" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button className="footer-button" aria-label="Refresh">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.333 2.667v4h4M14.667 13.333v-4h-4M2.44 5.333a6 6 0 019.427-2.42l2.8 2.754M1.333 10.333l2.8 2.754a6 6 0 009.427-2.42" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button className="footer-button" aria-label="Link">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.667 8.667a3.333 3.333 0 005.026.36l2-2A3.334 3.334 0 108.78 2.113l-1.147 1.14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9.333 7.333a3.333 3.333 0 00-5.026-.36l-2 2a3.334 3.334 0 004.913 4.914l1.14-1.14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <span className="citations">Citations</span>
        </div> */}
    </div>
  );
};

export default RecommendationCard;
